import { Component } from 'react';

import ErrorFallback from './ErrorFallback';
import { SentryErrorBoundary, captureException } from '@/utils/sentry';

const RELOAD_FLAG = 'insidePanama_chunkReload';
const CHUNK_RE = /Loading chunk \d+ failed|Loading CSS chunk \d+ failed|Importing a module script failed|Failed to fetch dynamically imported module|ChunkLoadError/i;

/**
 * Detect stale-deploy chunk load errors. When a React.lazy() route fails
 * to load its chunk (the cached SW manifest references a hash that no
 * longer exists in /dist after a redeploy), we get this signature in the
 * error. Recover by unregistering the SW and reloading once per session.
 *
 * Returns true if a recovery was triggered (caller should not render
 * the error UI in that case — the page is about to reload).
 */
function maybeRecoverFromChunkError(error) {
  const stack = String((error && (error.stack || error.message)) || error || '');
  if (!CHUNK_RE.test(stack)) return false;
  // 30-second rolling cooldown so multiple chunk failures don't loop the
  // reload, while still allowing recovery after a later deploy. Matches
  // the inline handler in index.html.
  try {
    const now = Date.now();
    const last = parseInt(sessionStorage.getItem(RELOAD_FLAG) || '0', 10);
    if (now - last < 30000) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(now));
  } catch { /* session storage blocked */ }
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {})
      .finally(() => window.location.reload());
  } else {
    window.location.reload();
  }
  return true;
}

// Plain class boundary used when Sentry isn't configured (dev or no DSN).
// In production with VITE_SENTRY_DSN set, ErrorBoundary delegates to
// Sentry.ErrorBoundary so caught errors reach the dashboard.
class FallbackBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, recovering: false };
  }
  static getDerivedStateFromError(error) {
    // Detect chunk errors here so the fallback UI never flashes during
    // the brief window before reload kicks in.
    const recovering = CHUNK_RE.test(String((error && (error.stack || error.message)) || error || ''));
    return { hasError: true, recovering };
  }
  componentDidCatch(error, info) {
    if (maybeRecoverFromChunkError(error)) return;
    if (typeof console !== 'undefined') {
      console.error('[InsidePanama] Render error', error, info);
    }
    try {
      captureException(error, { extra: info });
    } catch {
      /* Sentry not initialized — already logged above */
    }
  }
  reset = () => this.setState({ hasError: false, recovering: false });
  render() {
    if (this.state.hasError) {
      // While the page reloads, render nothing rather than flashing
      // the error UI for 100 ms.
      if (this.state.recovering) return null;
      return <ErrorFallback resetError={this.reset} />;
    }
    return this.props.children;
  }
}

// Public boundary: use this everywhere. In production with Sentry on, it's
// the SDK's boundary (richer reporting, better hooks). Otherwise it's the
// plain class above.
export default function ErrorBoundary({ children }) {
  const sentryReady = import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN);

  if (sentryReady && SentryErrorBoundary) {
    return (
      <SentryErrorBoundary
        fallback={({ resetError, error }) => {
          // Chunk-load error during a deploy window → reload once instead
          // of showing the angry red fallback. Matches the FallbackBoundary
          // recovery path so Sentry-on and Sentry-off behave the same.
          if (maybeRecoverFromChunkError(error)) return null;
          return <ErrorFallback resetError={resetError} />;
        }}
        showDialog={false}
      >
        {children}
      </SentryErrorBoundary>
    );
  }
  return <FallbackBoundary>{children}</FallbackBoundary>;
}
